import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { getJob, processJob, getTenantDomains, addTenantDomain, removeTenantDomain,
         getJobRows, getJobPricing, createPaymentSession, validateDiscountCode } from "../lib/api";
import { clearSession, getUser } from "../lib/auth";
import { logEvent, logError } from "../lib/log";

// ── Constants ─────────────────────────────────────────────────────────────────

const FREE_RECORDS = 10;

const STAGES = [
  { key: "uploaded",   label: "Uploaded"   },
  { key: "pending",    label: "Queued"     },
  { key: "processing", label: "Processing" },
  { key: "complete",   label: "Complete"   },
];
const STAGE_KEYS = STAGES.map((s) => s.key);

function stageIndex(status) {
  if (status === "failed") return -1;
  if (status === "awaiting_payment") return 1;
  const i = STAGE_KEYS.indexOf(status);
  return i === -1 ? 1 : i;
}

function formatDate(raw) {
  if (!raw) return "—";
  return new Date(raw).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function formatRows(n) {
  if (!n && n !== 0) return "—";
  return Number(n).toLocaleString();
}

function fmt$(n) {
  if (n == null) return "—";
  return "$" + Number(n).toFixed(2);
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ProcessJob() {
  const { jobId }         = useParams();
  const navigate          = useNavigate();
  const user              = getUser();
  const [searchParams]    = useSearchParams();

  const paymentResult = searchParams.get("payment"); // 'success' | 'cancelled'

  // Core job state
  const [job,       setJob]       = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [starting,  setStarting]  = useState(false);
  const [startErr,  setStartErr]  = useState("");

  // Domains
  const [domains,   setDomains]   = useState([]);
  const [domInput,  setDomInput]  = useState("");
  const [domAdding, setDomAdding] = useState(false);
  const [domErr,    setDomErr]    = useState("");

  // Checkout flow: null | 'preview' | 'pricing'
  const [checkoutStep,    setCheckoutStep]    = useState(null);
  const [selectedMode,    setSelectedMode]    = useState(null); // 'new' | 'all'
  const [previewRows,     setPreviewRows]     = useState([]);
  const [previewLoading,  setPreviewLoading]  = useState(false);
  const [pricing,         setPricing]         = useState(null);
  const [pricingLoading,  setPricingLoading]  = useState(false);
  const [discountCode,    setDiscountCode]    = useState("");
  const [discountResult,  setDiscountResult]  = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountErr,     setDiscountErr]     = useState("");
  const [tcAccepted,      setTcAccepted]      = useState(false);
  const [payLoading,      setPayLoading]      = useState(false);

  // Success modal
  const [showSuccess, setShowSuccess] = useState(paymentResult === "success");

  const pollRef = useRef(null);

  const fetchJob = useCallback(async () => {
    try {
      const res = await getJob(jobId);
      const j   = res.job || res;
      setJob(j);
      setError("");
      // Clear starting spinner once trial completes (pending + processed > 0) or fails
      const pc = (j.processed_count || j.processed_records || 0);
      if ((j.status === "pending" && pc > 0) ||
          j.status === "complete" || j.status === "failed") {
        setStarting(false);
      }
    } catch (e) {
      setError(e.message || "Could not load job.");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchJob();
    getTenantDomains().then(r => setDomains(r.domains || [])).catch(() => {});
    logEvent('page_view', { page: 'process', job_id: jobId });
  }, [fetchJob]);

  // Poll while trial actively running or full processing
  useEffect(() => {
    if (!job) return;
    const active = job.status === "processing" ||
                   (job.status === "pending" && starting);
    if (active) {
      pollRef.current = setInterval(fetchJob, 4000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [job?.status, starting, fetchJob]);

  function handleLogout() { clearSession(); navigate("/login"); }

  // ── Trial run (first FREE_RECORDS records, no payment) ───────────────────
  async function handleStartTrial() {
    setStarting(true);
    setStartErr("");
    logEvent('job_trial_started', { job_id: jobId });
    try {
      await processJob(jobId, { limit: FREE_RECORDS });
    } catch (e) {
      if (!e.message?.includes("Failed to fetch") && !e.message?.includes("network")) {
        setStartErr(e.message || "Failed to start. Please try again.");
        logError('trial_start', e);
        setStarting(false);
        return;
      }
    }
    await fetchJob();
    setStarting(false);
  }

  // ── Checkout: load preview rows ───────────────────────────────────────────
  async function handleOpenCheckout(mode) {
    setSelectedMode(mode);
    setCheckoutStep("preview");
    setPreviewLoading(true);
    try {
      const res = await getJobRows(jobId, { page: 1, limit: 5 });
      setPreviewRows(res.rows || []);
    } catch (e) {
      setPreviewRows([]);
    } finally {
      setPreviewLoading(false);
    }
  }

  // ── Checkout: confirm preview → load pricing ──────────────────────────────
  async function handleConfirmPreview() {
    setCheckoutStep("pricing");
    setPricingLoading(true);
    setDiscountResult(null);
    setDiscountCode("");
    setTcAccepted(false);
    try {
      const res = await getJobPricing(jobId, selectedMode || 'all');
      setPricing(res);
    } catch (e) {
      setPricing(null);
    } finally {
      setPricingLoading(false);
    }
  }

  // ── Checkout: apply discount code ─────────────────────────────────────────
  async function handleApplyDiscount(e) {
    e.preventDefault();
    if (!discountCode.trim()) return;
    setDiscountLoading(true);
    setDiscountErr("");
    try {
      const res = await validateDiscountCode(jobId, discountCode.trim());
      setDiscountResult(res);
    } catch (e) {
      setDiscountErr(e.message || "Invalid code.");
      setDiscountResult(null);
    } finally {
      setDiscountLoading(false);
    }
  }

  // ── Checkout: proceed to payment ─────────────────────────────────────────
  async function handlePay() {
    setPayLoading(true);
    logEvent('payment_initiated', { job_id: jobId, amount: pricing?.amount_usd, discount: discountResult?.valid ? discountCode : null });
    try {
      const code = discountResult?.valid ? discountCode.trim() : undefined;
      const res  = await createPaymentSession(jobId, code);
      if (res.session_url) {
        window.location.href = res.session_url;
      } else if (res.free) {
        window.location.href = `/process/${jobId}?payment=success`;
      }
    } catch (e) {
      logError('payment', e);
      setStartErr(e.message || "Payment setup failed. Please try again.");
      setPayLoading(false);
    }
  }

  // ── Domains ───────────────────────────────────────────────────────────────
  async function handleAddDomain(e) {
    e.preventDefault();
    const val = domInput.trim();
    if (!val) return;
    setDomAdding(true);
    setDomErr("");
    try {
      const r = await addTenantDomain(val);
      setDomains(r.domains || []);
      setDomInput("");
    } catch (e) {
      setDomErr(e.message || "Could not add domain.");
    } finally {
      setDomAdding(false);
    }
  }

  async function handleRemoveDomain(domain) {
    try {
      const r = await removeTenantDomain(domain);
      setDomains(r.domains || []);
    } catch (e) {
      setDomErr(e.message || "Could not remove domain.");
    }
  }

  // ── Derived state ─────────────────────────────────────────────────────────
  const isFailed        = job?.status === "failed";
  const isComplete      = job?.status === "complete";
  const isProcessing    = job?.status === "processing";
  const isAwaitingPay   = job?.status === "awaiting_payment";
  const processedCount  = job?.processed_count || job?.processed_records || 0;
  const isTrialDone     = job?.status === "pending" && processedCount > 0 && !starting;
  const currentStage    = isTrialDone ? 3 : (job ? stageIndex(job.status) : 1);
  const isActive        = isProcessing || (job?.status === "pending" && starting);
  // Vendor/address counts (new model) — fall back to record_count for older jobs
  const vendorCount     = job?.vendor_count ?? job?.record_count ?? 0;
  const addressCount    = job?.address_count ?? job?.record_count ?? 0;
  const newVendorCount  = job?.new_vendor_count ?? job?.new_record_count ?? vendorCount;
  const totalRecords    = vendorCount; // keep for legacy references
  const remainingRecords = Math.max(0, vendorCount - processedCount);

  // Human-readable summary e.g. "2 unique vendors · 3 address records"
  function vendorAddressSummary(vc, ac) {
    const vStr = `${formatRows(vc)} unique vendor${vc !== 1 ? 's' : ''}`;
    if (ac && ac !== vc) return `${vStr} · ${formatRows(ac)} address records`;
    return vStr;
  }

  // The effective amount after discount
  const baseAmount     = pricing?.amount_usd ?? 0;
  const finalAmount    = discountResult?.valid
    ? discountResult.final_amount
    : baseAmount;
  const isFreeCheckout = finalAmount === 0;

  // ── Success modal ─────────────────────────────────────────────────────────
  if (showSuccess) {
    return (
      <div style={styles.modalOverlay}>
        <div style={styles.modalCard}>
          <div style={styles.modalIcon}>✓</div>
          <h2 style={styles.modalTitle}>Payment Confirmed!</h2>
          <p style={styles.modalText}>
            We've started enriching <strong>{vendorAddressSummary(vendorCount, addressCount)}</strong> from <strong>{job?.file_name || `Job #${jobId}`}</strong>.
          </p>
          <p style={styles.modalText}>
            You'll receive an email when processing is complete.
          </p>
          <button style={styles.modalBtn} onClick={() => navigate("/dashboard")}>
            Go to Dashboard →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.topStripe} />
      <div style={styles.body}>
      {/* ── Sidebar ───────────────────────────────────────────────────────── */}
      <aside style={styles.sidebar}>
        <div style={styles.logoRow}>
          <div style={styles.logoMark}>M</div>
          <span style={styles.logoText}>MasterFileAI</span>
        </div>
        <nav style={styles.nav}>
          <Link to="/dashboard" style={{ textDecoration: "none" }}>
            <NavItem icon="▦" label="Dashboard" />
          </Link>
          <Link to="/upload" style={{ textDecoration: "none" }}>
            <NavItem icon="↑" label="Upload File" />
          </Link>
          <NavItem icon="⧗" label="Results" />
          <NavItem icon="⚙" label="Team" />
        </nav>
        <div style={styles.sidebarFooter}>
          <div style={styles.userChip}>
            <div style={styles.userAvatar}>{user?.full_name?.[0]?.toUpperCase() || "U"}</div>
            <div>
              <div style={styles.userName}>{user?.full_name || "User"}</div>
              <div style={styles.userRole}>{user?.role || "client"}</div>
            </div>
          </div>
          <button onClick={handleLogout} style={styles.logoutBtn}>Sign out</button>
        </div>
      </aside>

      {/* ── Main ──────────────────────────────────────────────────────────── */}
      <main style={styles.main}>
        <div style={styles.header}>
          <div>
            <Link to="/dashboard" style={styles.backLink}>← Back to dashboard</Link>
            <h1 style={styles.pageTitle}>Processing job</h1>
            <p style={styles.pageSub}>Job #{jobId}</p>
          </div>
        </div>

        {loading && <div style={styles.centerBox}><div style={styles.spinner} /><p style={styles.mutedText}>Loading job…</p></div>}
        {!loading && error && (
          <div style={styles.errorCard}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>⚠</div>
            <p style={{ color: "#f87171", margin: 0 }}>{error}</p>
            <button onClick={fetchJob} style={styles.retryBtn}>Retry</button>
          </div>
        )}

        {!loading && !error && job && (
          <>
            {/* File summary */}
            <div style={styles.summaryCard}>
              <div style={styles.summaryIcon}>📄</div>
              <div style={{ flex: 1 }}>
                <div style={styles.summaryFilename}>{job.original_filename || "—"}</div>
                <div style={styles.summaryMeta}>
                  <span>{vendorAddressSummary(vendorCount, addressCount)}</span>
                  <span style={styles.metaDot}>·</span>
                  <span>Uploaded {formatDate(job.created_at)}</span>
                  {job.job_type && (<><span style={styles.metaDot}>·</span><span style={{ textTransform: "capitalize" }}>{job.job_type}</span></>)}
                </div>
              </div>
              {isActive && <div style={styles.liveChip}><span style={styles.liveDot} />Live</div>}
            </div>

            {/* ── CHECKOUT: Preview Step ──────────────────────────────────── */}
            {checkoutStep === "preview" && (
              <div style={styles.pipelineCard}>
                <div style={styles.checkoutHeader}>
                  <button style={styles.backStepBtn} onClick={() => setCheckoutStep(null)}>← Back</button>
                  <div style={styles.checkoutStep}>Step 1 of 2</div>
                </div>
                <h2 style={styles.checkoutTitle}>Confirm your file</h2>
                <p style={styles.checkoutSub}>Review a sample of your uploaded records before proceeding to payment.</p>

                {previewLoading ? (
                  <div style={{ textAlign: "center", padding: "32px 0" }}><div style={styles.spinner} /></div>
                ) : (
                  <>
                    <div style={{ ...styles.previewTableWrap, overflowX: "auto" }}>
                      <table style={{ ...styles.previewTable, minWidth: 900 }}>
                        <thead>
                          <tr style={{ background: "#f8fafc" }}>
                            <th style={styles.previewTh}>#</th>
                            <th style={styles.previewTh}>Vendor #</th>
                            <th style={styles.previewTh}>Vendor Name</th>
                            <th style={styles.previewTh}>Addr Seq</th>
                            <th style={styles.previewTh}>Address 1</th>
                            <th style={styles.previewTh}>PO Box</th>
                            <th style={styles.previewTh}>City</th>
                            <th style={styles.previewTh}>State</th>
                            <th style={styles.previewTh}>Postal</th>
                            <th style={styles.previewTh}>Country</th>
                            <th style={styles.previewTh}>Phone</th>
                            <th style={styles.previewTh}>Email</th>
                            <th style={styles.previewTh}>Website</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewRows.map((row) => (
                            <tr key={row.row_number} style={{ borderBottom: "1px solid #f1f3f8" }}>
                              <td style={styles.previewTd}>{row.row_number}</td>
                              <td style={styles.previewTd}>{row.vendor_customer_num || "—"}</td>
                              <td style={{ ...styles.previewTd, fontWeight: 500, color: "#111827", whiteSpace: "nowrap" }}>{row.vendor_customer_name || "—"}</td>
                              <td style={styles.previewTd}>{row.address_seq || "—"}</td>
                              <td style={styles.previewTd}>{row.address_line_1 || "—"}</td>
                              <td style={styles.previewTd}>{row.po_box || "—"}</td>
                              <td style={styles.previewTd}>{row.city || "—"}</td>
                              <td style={styles.previewTd}>{row.state_province || "—"}</td>
                              <td style={styles.previewTd}>{row.zip_postal_code || "—"}</td>
                              <td style={styles.previewTd}>{row.country || "—"}</td>
                              <td style={styles.previewTd}>{row.phone || "—"}</td>
                              <td style={styles.previewTd}>{row.email || "—"}</td>
                              <td style={styles.previewTd}>{row.website_domain || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p style={{ color: "#9ca3af", fontSize: 12, marginTop: 8 }}>
                      Showing first {previewRows.length} of {formatRows(addressCount)} address records ({formatRows(vendorCount)} unique vendors).{" "}
                      <button style={styles.linkBtn} onClick={() => navigate(`/jobs/${jobId}/data`)}>View all data →</button>
                    </p>
                    <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
                      <button style={styles.primaryBtn} onClick={handleConfirmPreview}>
                        File looks correct — Continue →
                      </button>
                      <button style={styles.secondaryBtn} onClick={() => setCheckoutStep(null)}>Cancel</button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── CHECKOUT: Pricing Step ──────────────────────────────────── */}
            {checkoutStep === "pricing" && (
              <div style={styles.pipelineCard}>
                <div style={styles.checkoutHeader}>
                  <button style={styles.backStepBtn} onClick={() => setCheckoutStep("preview")}>← Back</button>
                  <div style={styles.checkoutStep}>Step 2 of 2</div>
                </div>
                <h2 style={styles.checkoutTitle}>Review pricing</h2>
                <p style={styles.checkoutSub}>Confirm your order before proceeding to payment.</p>

                {pricingLoading ? (
                  <div style={{ textAlign: "center", padding: "32px 0" }}><div style={styles.spinner} /></div>
                ) : pricing ? (
                  <>
                    {/* Pricing breakdown */}
                    <div style={styles.pricingCard}>
                      <div style={styles.pricingRow}>
                        <span style={styles.pricingLabel}>File</span>
                        <span style={styles.pricingValue}>{pricing.file_name}</span>
                      </div>
                      <div style={styles.pricingRow}>
                        <span style={styles.pricingLabel}>Unique vendors in file</span>
                        <span style={styles.pricingValue}>{formatRows(pricing.total_records)}</span>
                      </div>
                      <div style={styles.pricingRow}>
                        <span style={styles.pricingLabel}>
                          {pricing.mode === 'new' ? 'New vendors (not yet enriched)' : 'Vendors to process'}
                        </span>
                        <span style={styles.pricingValue}>{formatRows(pricing.records_to_process)}</span>
                      </div>
                      {pricing.free_records > 0 && (
                        <div style={{ ...styles.pricingRow, color: "#10b981" }}>
                          <span style={styles.pricingLabel}>Free vendors</span>
                          <span style={styles.pricingValue}>−{formatRows(pricing.free_records)}</span>
                        </div>
                      )}
                      <div style={{ ...styles.pricingRow, borderTop: "1px solid #e2e6ef", paddingTop: 12, marginTop: 4 }}>
                        <span style={styles.pricingLabel}>Billable vendors</span>
                        <span style={{ ...styles.pricingValue, fontWeight: 700 }}>{formatRows(pricing.billable_records)}</span>
                      </div>
                      {/* Tiered breakdown */}
                      {(pricing.breakdown || []).map((b, i) => (
                        <div key={i} style={{ ...styles.pricingRow, color: "#6b7280", fontSize: 12 }}>
                          <span style={styles.pricingLabel}>
                            {i === 0 ? `First` : i === (pricing.breakdown.length - 1) ? `Remaining` : `Next`}{" "}
                            {formatRows(b.records)} × {fmt$(b.rate)}
                          </span>
                          <span style={styles.pricingValue}>{fmt$(b.cost)}</span>
                        </div>
                      ))}
                      {pricing.minimum_applied && (
                        <div style={{ ...styles.pricingRow, color: "#d97706" }}>
                          <span style={styles.pricingLabel}>
                            Minimum charge applied
                            <span style={{ fontSize: 11, marginLeft: 6, color: "#9ca3af" }}>
                              (min. {fmt$(pricing.minimum_usd)}/file)
                            </span>
                          </span>
                          <span style={styles.pricingValue}>
                            +{fmt$(Math.round((pricing.minimum_usd - (pricing.breakdown || []).reduce((s, b) => s + b.cost, 0)) * 100) / 100)}
                          </span>
                        </div>
                      )}
                      {discountResult?.valid && (
                        <div style={{ ...styles.pricingRow, color: "#10b981" }}>
                          <span style={styles.pricingLabel}>Discount ({discountCode.toUpperCase()})</span>
                          <span style={styles.pricingValue}>−{fmt$(discountResult.discount_amount)}</span>
                        </div>
                      )}
                      <div style={{ ...styles.pricingRow, borderTop: "2px solid #e2e6ef", paddingTop: 14, marginTop: 8 }}>
                        <span style={{ ...styles.pricingLabel, fontSize: 16, fontWeight: 700, color: "#111827" }}>Total</span>
                        <span style={{ ...styles.pricingValue, fontSize: 20, fontWeight: 800, color: "#111827" }}>
                          {fmt$(finalAmount)}
                        </span>
                      </div>
                    </div>

                    {/* Discount code */}
                    <div style={{ marginTop: 20 }}>
                      <div style={{ color: "#374151", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Discount code</div>
                      <form onSubmit={handleApplyDiscount} style={{ display: "flex", gap: 8 }}>
                        <input
                          style={styles.discountInput}
                          type="text"
                          placeholder="Enter code"
                          value={discountCode}
                          onChange={e => { setDiscountCode(e.target.value); setDiscountResult(null); setDiscountErr(""); }}
                          disabled={discountLoading}
                        />
                        <button type="submit" style={styles.discountBtn} disabled={discountLoading || !discountCode.trim()}>
                          {discountLoading ? "…" : "Apply"}
                        </button>
                      </form>
                      {discountErr && <div style={{ color: "#ef4444", fontSize: 12, marginTop: 6 }}>{discountErr}</div>}
                      {discountResult?.valid && (
                        <div style={{ color: "#10b981", fontSize: 12, marginTop: 6 }}>
                          ✓ {discountResult.is_free ? "100% discount applied — no charge!" : `Discount applied: −${fmt$(discountResult.discount_amount)}`}
                        </div>
                      )}
                    </div>

                    {/* T&C */}
                    <label style={styles.tcRow}>
                      <input
                        type="checkbox"
                        checked={tcAccepted}
                        onChange={e => setTcAccepted(e.target.checked)}
                        style={{ width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
                      />
                      <span style={{ color: "#374151", fontSize: 13, lineHeight: 1.5 }}>
                        I agree to the{" "}
                        <Link to="/terms" target="_blank" style={{ color: "#0176CE", textDecoration: "none" }}>Terms of Service</Link>
                        {" "}and confirm the total of <strong>{fmt$(finalAmount)}</strong> will be charged.
                      </span>
                    </label>

                    {startErr && <div style={{ color: "#ef4444", fontSize: 13, marginTop: 8 }}>{startErr}</div>}

                    <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
                      <button
                        style={{ ...styles.primaryBtn, opacity: (!tcAccepted || payLoading) ? 0.5 : 1 }}
                        onClick={handlePay}
                        disabled={!tcAccepted || payLoading}
                      >
                        {payLoading ? "Redirecting…" : isFreeCheckout ? "Confirm & Start Processing →" : "Proceed to Payment →"}
                      </button>
                      <button style={styles.secondaryBtn} onClick={() => setCheckoutStep(null)} disabled={payLoading}>Cancel</button>
                    </div>
                  </>
                ) : (
                  <p style={{ color: "#ef4444", fontSize: 13 }}>Could not load pricing. Please try again.</p>
                )}
              </div>
            )}

            {/* ── NORMAL JOB VIEW (no checkout step) ─────────────────────── */}
            {checkoutStep === null && !isFailed && (
              <div style={styles.pipelineCard}>
                {/* Pipeline */}
                <div style={styles.pipelineRow}>
                  {STAGES.map((stage, idx) => {
                    const done    = idx < currentStage;
                    const current = idx === currentStage;
                    return (
                      <div key={stage.key} style={styles.pipelineStep}>
                        {idx > 0 && (
                          <div style={{ ...styles.connector, background: done || current ? "#0176CE" : "#e2e8f0" }} />
                        )}
                        <div style={{
                          ...styles.stageCircle,
                          background: done ? "#0176CE" : current ? "#0176CE" : "#f0f4f8",
                          border:     done || current ? "none" : "2px solid #e2e8f0",
                          color:      done || current ? "#fff" : "#9ca3af",
                          boxShadow:  current ? "0 0 0 4px rgba(1,118,206,0.15)" : "none",
                        }}>
                          {done ? "✓" : current && isActive ? <SpinnerDot /> : idx + 1}
                        </div>
                        <div style={{
                          ...styles.stageLabel,
                          color:      done ? "#0176CE" : current ? "#111827" : "#9ca3af",
                          fontWeight: current ? 600 : 400,
                        }}>{stage.label}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Status message */}
                <div style={styles.statusMsg}>
                  {job.status === "uploaded"        && "File received. Waiting to be queued…"}
                  {job.status === "pending" && !isTrialDone && !starting && "Your file is ready. Choose how to process it below."}
                  {job.status === "pending" && starting && "Starting enrichment…"}
                  {isTrialDone && `Preview complete — ${formatRows(processedCount)} vendor${processedCount !== 1 ? 's' : ''} enriched.`}
                  {isProcessing && `Enriching your vendors… ${processedCount ? formatRows(processedCount) + " of " + formatRows(vendorCount) + " done" : "hang tight"}`}
                  {isComplete   && "All vendors have been enriched successfully!"}
                  {isAwaitingPay && "Payment pending — complete your purchase to start processing."}
                </div>

                {/* Progress bar */}
                {isProcessing && totalRecords > 0 && processedCount > 0 && (
                  <div style={styles.progressTrack}>
                    <div style={{ ...styles.progressFill, width: `${Math.min(100, Math.round((processedCount / totalRecords) * 100))}%` }} />
                  </div>
                )}

                {/* ── Trial complete state ───────────────────────────────── */}
                {isTrialDone && (
                  <div style={styles.trialDoneBox}>
                    <div style={styles.trialDoneTick}>✓</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: "#111827", fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
                        Free trial complete
                      </div>
                      <div style={{ color: "#6b7280", fontSize: 13 }}>
                        {formatRows(processedCount)} of {formatRows(vendorCount)} unique vendors enriched for free.
                        {remainingRecords > 0 && ` ${formatRows(remainingRecords)} vendors remaining.`}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <button style={styles.secondaryBtn} onClick={() => navigate(`/results/${jobId}`)}>
                        View trial results →
                      </button>
                      {remainingRecords > 0 && (
                        <button style={styles.primaryBtn} onClick={() => handleOpenCheckout('all')}>
                          Process remaining {formatRows(remainingRecords)} vendors →
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Awaiting payment state ─────────────────────────────── */}
                {isAwaitingPay && (
                  <div style={styles.awaitingPayBox}>
                    <div style={{ fontSize: 28, marginBottom: 10 }}>💳</div>
                    <div style={{ color: "#111827", fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
                      Payment not completed
                    </div>
                    <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 16 }}>
                      Your file is ready to enrich. Resume payment to start processing.
                    </div>
                    <button style={styles.primaryBtn} onClick={() => handleOpenCheckout('all')}>
                      Resume payment →
                    </button>
                  </div>
                )}

                {/* ── Initial pending: show trial + full file options ────── */}
                {job.status === "pending" && !isTrialDone && !starting && (
                  <>
                    {/* Domains */}
                    <div style={styles.domainsCard}>
                      <div style={styles.domainsHeader}>
                        <div>
                          <div style={styles.domainsTitle}>Your intercompany domains</div>
                          <div style={styles.domainsDesc}>Records matching these domains are flagged as internal and skipped from AI enrichment.</div>
                        </div>
                      </div>
                      <div style={styles.domainChips}>
                        {domains.map(d => (
                          <div key={d.domain_id} style={styles.domainChip}>
                            <span>{d.domain}</span>
                            {d.is_primary ? <span style={styles.primaryBadge}>primary</span> : null}
                            <button style={styles.chipRemove} onClick={() => handleRemoveDomain(d.domain)}>✕</button>
                          </div>
                        ))}
                        {domains.length === 0 && <span style={{ color: "#9ca3af", fontSize: 13 }}>No domains added yet.</span>}
                      </div>
                      <form onSubmit={handleAddDomain} style={styles.domainForm}>
                        <input
                          style={styles.domainInput}
                          type="text"
                          placeholder="Add domain (e.g. subsidiary.com)"
                          value={domInput}
                          onChange={e => setDomInput(e.target.value)}
                          disabled={domAdding}
                        />
                        <button type="submit" style={styles.domainAddBtn} disabled={domAdding || !domInput.trim()}>
                          {domAdding ? "Adding…" : "+ Add"}
                        </button>
                      </form>
                      {domErr && <div style={{ color: "#ef4444", fontSize: 12, marginTop: 6 }}>{domErr}</div>}
                    </div>

                    <div style={styles.startBox}>
                      <div style={styles.startCard}>
                        <div style={styles.startCardLabel}>Free trial</div>
                        <div style={styles.startCardTitle}>{FREE_RECORDS} vendors</div>
                        <div style={styles.startCardDesc}>Enrich the first {FREE_RECORDS} vendors at no cost. See results before you commit.</div>
                        <button style={styles.primaryBtn} onClick={handleStartTrial}>
                          Start free trial →
                        </button>
                      </div>
                      {/* New vendors card — only show when there are new vendors */}
                      {(newVendorCount == null || newVendorCount > 0) && (
                        <div style={styles.startCard}>
                          <div style={styles.startCardLabel}>New vendors</div>
                          <div style={styles.startCardTitle}>{formatRows(newVendorCount ?? vendorCount)} unique vendors</div>
                          <div style={styles.startCardDesc}>
                            {newVendorCount == null || newVendorCount === vendorCount
                              ? "All vendors are new — not previously enriched."
                              : `${formatRows(newVendorCount)} vendors not previously enriched in your master file.`}
                          </div>
                          <button style={styles.primaryBtn} onClick={() => handleOpenCheckout('new')}>
                            Enrich new vendors →
                          </button>
                        </div>
                      )}
                      {/* Re-enrich all — show when some or all vendors already exist */}
                      {(newVendorCount != null && newVendorCount < vendorCount) && (
                        <div style={styles.startCard}>
                          <div style={styles.startCardLabel}>Re-enrich all</div>
                          <div style={styles.startCardTitle}>{vendorAddressSummary(vendorCount, addressCount)}</div>
                          <div style={styles.startCardDesc}>
                            {newVendorCount === 0
                              ? "All vendors already exist in your master file. Re-enrich to refresh with the latest data."
                              : "Refresh every vendor in this file with the latest AI enrichment data."}
                          </div>
                          <button style={{ ...styles.primaryBtn, background: "#111827" }} onClick={() => handleOpenCheckout('all')}>
                            Re-enrich all {formatRows(vendorCount)} vendors →
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Starting spinner */}
                {starting && (
                  <div style={{ textAlign: "center", padding: "16px 0" }}>
                    <div style={styles.spinner} />
                  </div>
                )}

                {startErr && <div style={{ textAlign: "center", color: "#ef4444", fontSize: 13, marginTop: 8 }}>{startErr}</div>}

                {/* Complete CTA */}
                {isComplete && (
                  <div style={{ textAlign: "center", paddingTop: 8 }}>
                    <button style={styles.primaryBtn} onClick={() => navigate(`/results/${jobId}`)}>
                      View results →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Failed */}
            {isFailed && (
              <div style={styles.failedCard}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>✗</div>
                <div style={styles.failedTitle}>Processing failed</div>
                {job.error_message && <div style={styles.failedMsg}>{job.error_message}</div>}
                <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 20 }}>
                  <Link to="/upload" style={{ textDecoration: "none" }}>
                    <button style={styles.primaryBtn}>Upload a new file</button>
                  </Link>
                  <Link to="/dashboard" style={{ textDecoration: "none" }}>
                    <button style={styles.secondaryBtn}>Go to dashboard</button>
                  </Link>
                </div>
              </div>
            )}

            {/* View uploaded data */}
            {checkoutStep === null && (
              <div style={{ marginBottom: 20 }}>
                <button style={styles.secondaryBtn} onClick={() => navigate(`/jobs/${jobId}/data`)}>
                  View uploaded data →
                </button>
              </div>
            )}

            {/* Job details */}
            {checkoutStep === null && (
              <div style={styles.detailsCard}>
                <div style={styles.detailsTitle}>Job details</div>
                <div style={styles.detailsGrid}>
                  <DetailRow label="Job ID"    value={job.job_id} />
                  <DetailRow label="File"      value={job.original_filename} />
                  <DetailRow label="Records"   value={formatRows(job.total_records)} />
                  <DetailRow label="Status"    value={job.status?.replace(/_/g, " ")} capitalize />
                  <DetailRow label="Type"      value={job.job_type} capitalize />
                  <DetailRow label="Submitted" value={formatDate(job.created_at)} />
                  {job.completed_on_utc && <DetailRow label="Completed" value={formatDate(job.completed_on_utc)} />}
                </div>
              </div>
            )}
          </>
        )}
      </main>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function NavItem({ icon, label, active }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px", borderRadius: 8,
      background: active ? "rgba(1,118,206,0.08)" : "transparent",
      color: active ? "#0176CE" : "#6b7280",
      fontSize: 14, fontWeight: active ? 600 : 400,
      cursor: "pointer", marginBottom: 2,
    }}>
      <span style={{ fontSize: 16, width: 20, textAlign: "center" }}>{icon}</span>
      {label}
    </div>
  );
}

function DetailRow({ label, value, capitalize }) {
  if (!value && value !== 0) return null;
  return (
    <div style={styles.detailRow}>
      <span style={styles.detailLabel}>{label}</span>
      <span style={{ ...styles.detailValue, textTransform: capitalize ? "capitalize" : "none" }}>{value}</span>
    </div>
  );
}

function SpinnerDot() {
  return (
    <div style={{
      width: 12, height: 12,
      border: "2px solid rgba(255,255,255,0.4)",
      borderTop: "2px solid #fff",
      borderRadius: "50%",
      animation: "spin 0.8s linear infinite",
    }} />
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  page:          { display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', system-ui, sans-serif" },
  topStripe:     { height: 4, background: "linear-gradient(90deg, #0176CE, #F37428)", flexShrink: 0 },
  body:          { display: "flex", flex: 1 },
  sidebar:       { width: 220, background: "#fff", borderRight: "1px solid #e2e8f0", display: "flex", flexDirection: "column", padding: "24px 16px", flexShrink: 0 },
  logoRow:       { display: "flex", alignItems: "center", gap: 10, marginBottom: 36, paddingLeft: 6 },
  logoMark:      { width: 32, height: 32, borderRadius: 9, background: "#F37428", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 16, flexShrink: 0 },
  logoText:      { color: "#111827", fontWeight: 700, fontSize: 15, letterSpacing: "-0.3px" },
  nav:           { flex: 1 },
  sidebarFooter: { borderTop: "1px solid #e2e8f0", paddingTop: 16 },
  userChip:      { display: "flex", alignItems: "center", gap: 10, marginBottom: 10 },
  userAvatar:    { width: 32, height: 32, borderRadius: "50%", background: "#F37428", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 13, flexShrink: 0 },
  userName:      { color: "#1f2937", fontSize: 13, fontWeight: 600 },
  userRole:      { color: "#6b7280", fontSize: 11, textTransform: "capitalize" },
  logoutBtn:     { background: "transparent", border: "1px solid #e2e8f0", borderRadius: 6, color: "#6b7280", cursor: "pointer", fontSize: 12, padding: "6px 12px", width: "100%", textAlign: "left" },
  main:          { flex: 1, padding: "40px 48px", overflowY: "auto" },
  header:        { marginBottom: 28 },
  backLink:      { color: "#6b7280", fontSize: 13, textDecoration: "none", display: "block", marginBottom: 8 },
  pageTitle:     { color: "#111827", fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: "-0.5px" },
  pageSub:       { color: "#6b7280", fontSize: 14, margin: "4px 0 0" },
  centerBox:     { textAlign: "center", padding: "80px 24px", display: "flex", flexDirection: "column", alignItems: "center" },
  mutedText:     { color: "#6b7280", marginTop: 16, fontSize: 14 },
  spinner:       { width: 32, height: 32, border: "3px solid #e2e8f0", borderTop: "3px solid #0176CE", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  errorCard:     { background: "#fff", border: "1px solid #fecaca", borderRadius: 12, padding: "40px 24px", textAlign: "center", marginBottom: 24 },
  retryBtn:      { marginTop: 16, background: "transparent", border: "1px solid #0176CE", borderRadius: 6, color: "#0176CE", cursor: "pointer", fontSize: 13, padding: "7px 16px" },
  summaryCard:   { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 24px", display: "flex", alignItems: "center", gap: 16, marginBottom: 20 },
  summaryIcon:   { fontSize: 32, flexShrink: 0 },
  summaryFilename:{ color: "#111827", fontWeight: 600, fontSize: 16, marginBottom: 4, wordBreak: "break-all" },
  summaryMeta:   { color: "#6b7280", fontSize: 13, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 },
  metaDot:       { color: "#d1d5db" },
  liveChip:      { display: "flex", alignItems: "center", gap: 6, background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 20, padding: "4px 10px", color: "#10b981", fontSize: 12, fontWeight: 600, flexShrink: 0 },
  liveDot:       { width: 7, height: 7, borderRadius: "50%", background: "#10b981", display: "inline-block", animation: "pulse 1.5s ease-in-out infinite" },
  pipelineCard:  { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "32px 40px", marginBottom: 20 },
  pipelineRow:   { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, position: "relative" },
  pipelineStep:  { display: "flex", flexDirection: "column", alignItems: "center", gap: 10, position: "relative", flex: 1 },
  connector:     { position: "absolute", left: "-50%", top: 19, width: "100%", height: 2, zIndex: 0 },
  stageCircle:   { width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, zIndex: 1, flexShrink: 0, transition: "all 0.3s ease" },
  stageLabel:    { fontSize: 12, letterSpacing: "0.02em", transition: "color 0.3s ease" },
  statusMsg:     { textAlign: "center", color: "#374151", fontSize: 15, marginBottom: 20 },
  progressTrack: { background: "#e2e8f0", borderRadius: 6, height: 8, overflow: "hidden", marginBottom: 24 },
  progressFill:  { height: "100%", borderRadius: 6, background: "linear-gradient(90deg, #0176CE, #F37428)", transition: "width 0.6s ease" },
  trialDoneBox:  { display: "flex", alignItems: "center", gap: 16, background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 10, padding: "20px 24px", marginBottom: 20, flexWrap: "wrap" },
  trialDoneTick: { width: 36, height: 36, borderRadius: "50%", background: "#10b981", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 18, flexShrink: 0 },
  awaitingPayBox:{ textAlign: "center", padding: "32px 24px", background: "rgba(245,158,11,0.05)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 10, marginBottom: 20 },
  domainsCard:   { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "20px 24px", marginBottom: 20 },
  domainsHeader: { marginBottom: 14 },
  domainsTitle:  { fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 4 },
  domainsDesc:   { fontSize: 12, color: "#6b7280", lineHeight: 1.5 },
  domainChips:   { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14, minHeight: 28 },
  domainChip:    { display: "flex", alignItems: "center", gap: 6, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 20, padding: "4px 10px 4px 12px", fontSize: 13, color: "#374151" },
  primaryBadge:  { fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#0176CE", background: "rgba(1,118,206,0.1)", borderRadius: 4, padding: "1px 5px" },
  chipRemove:    { background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 12, padding: "0 2px", lineHeight: 1 },
  domainForm:    { display: "flex", gap: 8, alignItems: "center" },
  domainInput:   { flex: 1, maxWidth: 300, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 7, color: "#111827", fontSize: 13, outline: "none", padding: "7px 12px" },
  domainAddBtn:  { background: "#fff", border: "1px solid #0176CE", borderRadius: 7, color: "#0176CE", cursor: "pointer", fontSize: 13, fontWeight: 600, padding: "7px 16px" },
  startBox:      { display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap", paddingTop: 8, paddingBottom: 8 },
  startCard:     { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "24px 28px", display: "flex", flexDirection: "column", gap: 10, flex: "1 1 240px", maxWidth: 300 },
  startCardLabel:{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#6b7280" },
  startCardTitle:{ fontSize: 24, fontWeight: 700, color: "#111827", lineHeight: 1 },
  startCardDesc: { fontSize: 13, color: "#6b7280", lineHeight: 1.5 },
  primaryBtn:    { background: "#F37428", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600, padding: "11px 28px", whiteSpace: "nowrap" },
  secondaryBtn:  { background: "transparent", border: "1px solid #e2e8f0", borderRadius: 8, color: "#374151", cursor: "pointer", fontSize: 14, fontWeight: 500, padding: "11px 28px", whiteSpace: "nowrap" },
  failedCard:    { background: "#fff", border: "1px solid #fecaca", borderRadius: 12, padding: "40px 24px", textAlign: "center", marginBottom: 20 },
  failedTitle:   { color: "#ef4444", fontSize: 18, fontWeight: 700, marginBottom: 8 },
  failedMsg:     { color: "#6b7280", fontSize: 14, maxWidth: 480, margin: "0 auto" },
  detailsCard:   { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "24px" },
  detailsTitle:  { color: "#111827", fontWeight: 600, fontSize: 14, marginBottom: 16 },
  detailsGrid:   { display: "flex", flexDirection: "column", gap: 10 },
  detailRow:     { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f3f4f6" },
  detailLabel:   { color: "#6b7280", fontSize: 13 },
  detailValue:   { color: "#111827", fontSize: 13, fontWeight: 500 },
  // Checkout
  checkoutHeader:{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
  checkoutStep:  { fontSize: 12, color: "#9ca3af", fontWeight: 500 },
  checkoutTitle: { color: "#111827", fontSize: 20, fontWeight: 700, margin: "0 0 6px", letterSpacing: "-0.3px" },
  checkoutSub:   { color: "#6b7280", fontSize: 14, margin: "0 0 20px" },
  backStepBtn:   { background: "transparent", border: "none", color: "#0176CE", cursor: "pointer", fontSize: 13, fontWeight: 600, padding: 0 },
  previewTableWrap: { overflowX: "auto", borderRadius: 8, border: "1px solid #e2e8f0" },
  previewTable:  { width: "100%", borderCollapse: "collapse", minWidth: 500 },
  previewTh:     { color: "#6b7280", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", padding: "10px 14px", textAlign: "left", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" },
  previewTd:     { color: "#374151", fontSize: 13, padding: "11px 14px", verticalAlign: "middle" },
  linkBtn:       { background: "none", border: "none", color: "#0176CE", cursor: "pointer", fontSize: 12, padding: 0, textDecoration: "underline" },
  pricingCard:   { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "20px 24px" },
  pricingRow:    { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" },
  pricingLabel:  { color: "#6b7280", fontSize: 14, display: "flex", alignItems: "center", gap: 8 },
  pricingValue:  { color: "#374151", fontSize: 14, fontWeight: 500, textAlign: "right" },
  tierBadge:     { fontSize: 10, fontWeight: 600, color: "#0176CE", background: "rgba(1,118,206,0.1)", borderRadius: 4, padding: "2px 7px", letterSpacing: "0.02em" },
  discountInput: { flex: 1, maxWidth: 200, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 7, color: "#111827", fontSize: 13, outline: "none", padding: "7px 12px" },
  discountBtn:   { background: "#fff", border: "1px solid #0176CE", borderRadius: 7, color: "#0176CE", cursor: "pointer", fontSize: 13, fontWeight: 600, padding: "7px 16px" },
  tcRow:         { display: "flex", alignItems: "flex-start", gap: 10, marginTop: 20, cursor: "pointer" },
  // Success modal
  modalOverlay:  { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, fontFamily: "'Inter', system-ui, sans-serif" },
  modalCard:     { background: "#fff", borderRadius: 16, padding: "48px 40px", maxWidth: 460, width: "90%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" },
  modalIcon:     { width: 64, height: 64, borderRadius: "50%", background: "#F37428", color: "#fff", fontSize: 28, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" },
  modalTitle:    { color: "#111827", fontSize: 24, fontWeight: 700, margin: "0 0 12px", letterSpacing: "-0.5px" },
  modalText:     { color: "#6b7280", fontSize: 14, lineHeight: 1.7, margin: "0 0 8px" },
  modalBtn:      { marginTop: 24, background: "#F37428", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 15, fontWeight: 600, padding: "13px 32px" },
};
